import type { Permissao } from '@metaepi/shared';
import type { ReactNode } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { PaginaInicio } from '../features/inicio/PaginaInicio';
import { PaginaAgravo } from '../features/sistema/PaginaAgravo';
import { EmConstrucao, ErroRota, NaoEncontrada } from '../features/sistema/Paginas';
import { ExigirLogin, ExigirPermissao } from './Guardas';
import { Layout } from './Layout';
import { ITENS_FASES, ITENS_PRINCIPAIS } from './navegacao';

/** Carrega a tela sob demanda (o navegador só baixa o código quando precisa). */
const sobDemanda = (carregar: () => Promise<{ default: () => ReactNode }>) => ({
  lazy: async () => ({ Component: (await carregar()).default }),
});

const protegida = (
  permissao: Permissao,
  carregar: () => Promise<{ default: () => ReactNode }>,
) => ({
  lazy: async () => {
    const Pagina = (await carregar()).default;
    return {
      Component: () => (
        <ExigirPermissao permissao={permissao}>
          <Pagina />
        </ExigirPermissao>
      ),
    };
  },
});

export const rotas: RouteObject[] = [
  // Telas sem login
  {
    path: '/entrar',
    ...sobDemanda(() =>
      import('../features/auth/PaginaLogin').then((m) => ({ default: m.PaginaLogin })),
    ),
    errorElement: <ErroRota />,
  },
  {
    path: '/esqueci-senha',
    ...sobDemanda(() =>
      import('../features/auth/PaginaEsqueciSenha').then((m) => ({
        default: m.PaginaEsqueciSenha,
      })),
    ),
    errorElement: <ErroRota />,
  },
  {
    path: '/redefinir-senha',
    ...sobDemanda(() =>
      import('../features/auth/PaginaRedefinirSenha').then((m) => ({
        default: m.PaginaRedefinirSenha,
      })),
    ),
    errorElement: <ErroRota />,
  },
  {
    path: '/primeiro-acesso',
    ...sobDemanda(() =>
      import('../features/auth/PaginaPrimeiroAcesso').then((m) => ({
        default: m.PaginaPrimeiroAcesso,
      })),
    ),
    errorElement: <ErroRota />,
  },
  {
    path: '/ajuda/acesso',
    ...sobDemanda(() =>
      import('../features/ajuda/Paginas').then((m) => ({ default: m.PaginaAjudaAcesso })),
    ),
    errorElement: <ErroRota />,
  },
  // Telas com login
  {
    element: <ExigirLogin />,
    errorElement: <ErroRota />,
    children: [
      {
        path: '/',
        element: <Layout />,
        children: [
          { index: true, element: <PaginaInicio /> },
          ...[...ITENS_FASES, ...ITENS_PRINCIPAIS]
            .filter((item) => !item.pronto)
            .map((item) => ({
              path: item.caminho.slice(1),
              element: (
                <EmConstrucao
                  titulo={item.rotulo}
                  modulo={item.modulo}
                  descricao={item.descricao}
                />
              ),
            })),
          {
            path: 'linha-cuidado/pre_natal',
            ...protegida('prenatal:ver', () =>
              import('../features/prenatal/PaginaPainel').then((m) => ({
                default: m.PaginaPainelPrenatal,
              })),
            ),
          },
          {
            path: 'linha-cuidado/maternidade',
            ...protegida('maternidade:ver', () =>
              import('../features/maternidade/Paineis').then((m) => ({
                default: m.PaginaMaternidade,
              })),
            ),
          },
          {
            path: 'linha-cuidado/neonatologia',
            ...protegida('maternidade:ver', () =>
              import('../features/maternidade/Paineis').then((m) => ({
                default: m.PaginaNeonatologia,
              })),
            ),
          },
          {
            path: 'linha-cuidado/puericultura',
            ...protegida('puericultura:ver', () =>
              import('../features/puericultura/PaginaPuericultura').then((m) => ({
                default: m.PaginaPuericultura,
              })),
            ),
          },
          {
            path: 'seguimentos/:id',
            ...protegida('puericultura:ver', () =>
              import('../features/puericultura/PaginaSeguimento').then((m) => ({
                default: m.PaginaSeguimento,
              })),
            ),
          },
          {
            path: 'criancas/:id',
            ...protegida('puericultura:ver', () =>
              import('../features/puericultura/PaginaSeguimento').then((m) => ({
                default: m.PaginaCrianca,
              })),
            ),
          },
          {
            path: 'recem-nascidos/:id',
            ...protegida('maternidade:ver', () =>
              import('../features/maternidade/PaginaRecemNascido').then((m) => ({
                default: m.PaginaRecemNascido,
              })),
            ),
          },
          {
            path: 'titular',
            ...protegida('titular:atender', () =>
              import('../features/titular/PaginasTitular').then((m) => ({
                default: m.PaginaSolicitacoesTitular,
              })),
            ),
          },
          {
            path: 'titular/nova',
            ...protegida('titular:atender', () =>
              import('../features/titular/PaginasTitular').then((m) => ({
                default: m.PaginaNovaSolicitacaoTitular,
              })),
            ),
          },
          {
            path: 'titular/:id',
            ...protegida('titular:atender', () =>
              import('../features/titular/PaginasTitular').then((m) => ({
                default: m.PaginaSolicitacaoTitular,
              })),
            ),
          },
          {
            path: 'agenda',
            ...protegida('agenda:ver', () =>
              import('../features/agenda/PaginaAgenda').then((m) => ({ default: m.PaginaAgenda })),
            ),
          },
          {
            path: 'indicadores',
            ...protegida('indicadores:ver', () =>
              import('../features/indicadores/PaginaIndicadores').then((m) => ({
                default: m.PaginaIndicadores,
              })),
            ),
          },
          {
            path: 'notificacoes',
            ...protegida('notificacoes:ver', () =>
              import('../features/sinan/Notificacoes').then((m) => ({
                default: m.PaginaNotificacoes,
              })),
            ),
          },
          {
            path: 'notificacoes/:id',
            ...protegida('notificacoes:ver', () =>
              import('../features/sinan/PaginaRevisao').then((m) => ({ default: m.PaginaRevisao })),
            ),
          },
          {
            path: 'pacientes',
            ...protegida('pacientes:ver', () =>
              import('../features/pacientes/PaginaPacientes').then((m) => ({
                default: m.PaginaPacientes,
              })),
            ),
          },
          {
            path: 'pacientes/:id',
            ...protegida('pacientes:ver', () =>
              import('../features/pacientes/PaginaGestante').then((m) => ({
                default: m.PaginaGestante,
              })),
            ),
          },
          { path: 'agravos/:agravo', element: <PaginaAgravo /> },
          // Ajuda (D-062): todos os perfis
          {
            path: 'ajuda',
            ...sobDemanda(() =>
              import('../features/ajuda/Paginas').then((m) => ({ default: m.PaginaCentralAjuda })),
            ),
          },
          {
            path: 'ajuda/completo',
            ...sobDemanda(() =>
              import('../features/ajuda/Paginas').then((m) => ({ default: m.PaginaGuiaCompleto })),
            ),
          },
          {
            path: 'ajuda/:guia',
            ...sobDemanda(() =>
              import('../features/ajuda/Paginas').then((m) => ({ default: m.PaginaGuia })),
            ),
          },
          {
            path: 'conta/senha',
            ...sobDemanda(() =>
              import('../features/auth/PaginaTrocarSenha').then((m) => ({
                default: m.PaginaTrocarSenha,
              })),
            ),
          },
          {
            path: 'administracao',
            children: [
              {
                path: 'usuarios',
                ...protegida('usuarios:gerenciar', () =>
                  import('../features/admin/PaginaUsuarios').then((m) => ({
                    default: m.PaginaUsuarios,
                  })),
                ),
              },
              {
                path: 'usuarios/:id',
                ...protegida('usuarios:gerenciar', () =>
                  import('../features/admin/PaginaUsuario').then((m) => ({
                    default: m.PaginaUsuario,
                  })),
                ),
              },
              {
                path: 'unidades',
                ...protegida('unidades:gerenciar', () =>
                  import('../features/admin/PaginaUnidades').then((m) => ({
                    default: m.PaginaUnidades,
                  })),
                ),
              },
              {
                path: 'municipios',
                ...protegida('municipios:gerenciar', () =>
                  import('../features/admin/PaginaMunicipios').then((m) => ({
                    default: m.PaginaMunicipios,
                  })),
                ),
              },
              {
                path: 'pedidos',
                ...protegida('solicitacoes:atender', () =>
                  import('../features/admin/PaginaPedidos').then((m) => ({
                    default: m.PaginaPedidos,
                  })),
                ),
              },
              {
                path: 'auditoria',
                ...protegida('auditoria:ver', () =>
                  import('../features/admin/PaginaAuditoria').then((m) => ({
                    default: m.PaginaAuditoria,
                  })),
                ),
              },
              {
                path: 'acessos',
                ...protegida('acessos:ver', () =>
                  import('../features/admin/PaginaAcessos').then((m) => ({
                    default: m.PaginaAcessos,
                  })),
                ),
              },
              {
                path: 'sessoes',
                ...protegida('sessoes:gerenciar', () =>
                  import('../features/admin/PaginaSessoes').then((m) => ({
                    default: m.PaginaSessoes,
                  })),
                ),
              },
              {
                path: 'modulos',
                ...protegida('parametros:gerenciar', () =>
                  import('../features/admin/PaginaModulos').then((m) => ({
                    default: m.PaginaModulos,
                  })),
                ),
              },
            ],
          },
          { path: '*', element: <NaoEncontrada /> },
        ],
      },
    ],
  },
];

export const criarRoteador = () => createBrowserRouter(rotas);
